import { TFunction } from 'i18next';
import { isEqual } from 'lodash';
import { useSearchParams } from 'react-router';

import { DOCUMENT_NUMBER_FALLBACK_OPTIONS, REPLACEMENT_WEEKDAY_OPTIONS } from '../constants.ts';
import { GROUP_SEPARATOR } from '../pages/Assistance/useDocumentConfig';
import { FieldDefinition, isCustomFieldDefinition, isShownSavedOption } from './fieldsConfigData.ts';

export const SECTION_SETTINGS_PARAM = 'settings';

// ?settings=<section> on the tab URL, so a reload or a second window opened from the link keeps the view
export const useSectionSettingsView = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const setSettingsSection = (sectionKey: string | null) =>
        setSearchParams((params) => {
            if (sectionKey) {
                params.set(SECTION_SETTINGS_PARAM, sectionKey);
            } else {
                params.delete(SECTION_SETTINGS_PARAM);
            }
            return params;
        });
    return { settingsSectionKey: searchParams.get(SECTION_SETTINGS_PARAM), setSettingsSection };
};

export interface OverviewOption {
    number: string;
    name: string;
    value: unknown;
    defaultValue: unknown;
    valueType: string;
    isChanged: boolean;
}

interface OverviewSubfield {
    number: string;
    key: string;
    isUsed: boolean;
    options: OverviewOption[];
}

interface OverviewField {
    number: string;
    key: string;
    isUsed: boolean;
    options: OverviewOption[];
    subfields: OverviewSubfield[];
}

export const isUsedIn = (fieldsByName: Map<string, FieldDefinition>, key: string) => {
    const [parentKey] = key.split(GROUP_SEPARATOR);
    return !!fieldsByName.get(key)?.enabled && !!fieldsByName.get(parentKey)?.enabled;
};

const toOptions = (field: FieldDefinition, number: string): OverviewOption[] =>
    (field.options ?? [])
        .filter((option) => isShownSavedOption(option, field))
        .map((option, index) => ({
            number: `${number}.${index + 1}`,
            name: option.name,
            value: option.value,
            defaultValue: option.defaultValue,
            valueType: option.valueType,
            isChanged: !isEqual(option.value ?? null, option.defaultValue ?? null),
        }));

// backend order (by name, the same in every channel) keeps numbers comparable; `position` differs per channel
export const buildFieldSettingsOverview = (fields: FieldDefinition[]): OverviewField[] => {
    const parents = fields.filter((field) => !field.name.includes(GROUP_SEPARATOR));
    const ordered = [
        ...parents.filter((field) => !isCustomFieldDefinition(field)),
        ...parents.filter((field) => isCustomFieldDefinition(field)),
    ];
    return ordered.map((field, fieldIndex) => {
        const number = `${fieldIndex + 1}`;
        const options = toOptions(field, number);
        const subfields = fields.filter((subfield) => subfield.name.startsWith(`${field.name}${GROUP_SEPARATOR}`));
        return {
            number,
            key: field.name,
            isUsed: !!field.enabled,
            options,
            subfields: subfields.map((subfield, subfieldIndex) => {
                const subfieldNumber = `${number}.${options.length + subfieldIndex + 1}`;
                return {
                    number: subfieldNumber,
                    key: subfield.name,
                    isUsed: !!field.enabled && !!subfield.enabled,
                    options: toOptions(subfield, subfieldNumber),
                };
            }),
        };
    });
};

const CHOICES: Record<string, { value: unknown; labelKey: string }[]> = {
    delivery_date_range_replacement_weekday: REPLACEMENT_WEEKDAY_OPTIONS,
    document_number_fallback: DOCUMENT_NUMBER_FALLBACK_OPTIONS,
};

const isPrimitive = (value: unknown) => value === null || ['string', 'number', 'boolean'].includes(typeof value);

export const formatOptionValue = (name: string, value: unknown, t: TFunction): string => {
    if (value === null || value === undefined || value === '') {
        return t('configPage.document.overview.notSet');
    }
    if (typeof value === 'boolean') {
        return value ? t('configPage.document.overview.on') : t('configPage.document.overview.off');
    }
    const choice = CHOICES[name]?.find((option) => option.value === value);
    if (choice) {
        return t(choice.labelKey);
    }
    if (Array.isArray(value)) {
        return value.every(isPrimitive)
            ? value.join(', ')
            : t('configPage.document.overview.entries', { count: value.length });
    }
    if (typeof value === 'object') {
        const entries = Object.entries(value);
        return entries.every(([, entry]) => isPrimitive(entry))
            ? entries.map(([key, entry]) => `${key} → ${entry}`).join(', ')
            : t('configPage.document.overview.entries', { count: entries.length });
    }
    return String(value);
};
